import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { requireConfig } from '@onering/npm-package/web';
import keycloak, { getAccessToken, forceRefreshToken } from '../auth/keycloak';

const API_BASE = requireConfig('API_BASE_URL');

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isApiError = (err: unknown): err is ApiError =>
  typeof err === 'object' &&
  err !== null &&
  'name' in err &&
  (err as { name: unknown }).name === 'ApiError' &&
  'status' in err &&
  typeof (err as { status: unknown }).status === 'number';

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(async (cfg) => {
  await keycloak.updateToken(30).catch(() => undefined);
  const token = getAccessToken();
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

apiClient.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    if (!axios.isAxiosError(err) || err.response?.status !== 401) {
      return Promise.reject(err);
    }
    const original = err.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (!original || original._retry) {
      return Promise.reject(err);
    }
    original._retry = true;
    try {
      await forceRefreshToken();
      const token = getAccessToken();
      if (token && original.headers) {
        original.headers.Authorization = `Bearer ${token}`;
      }
      return apiClient(original);
    } catch {
      void keycloak.login({ idpHint: 'azure-ad' });
      return Promise.reject(err);
    }
  },
);

export const apiFetch = async <T>(
  path: string,
  options: AxiosRequestConfig = {},
): Promise<T> => {
  try {
    const resp = await apiClient({ url: path, ...options });
    if (resp.status === 204) return undefined as T;
    return resp.data as T;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const body = err.response.data ?? {};
      throw new ApiError(
        body.message || body.error || err.response.statusText || 'Request failed',
        err.response.status,
        body.code || body.errorCode,
      );
    }
    throw err;
  }
};
